import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Users, Lock, Settings, Search, ChevronDown, DollarSign, Trophy,
  Truck, Shield, Star, X, Flame, CheckCircle2, FileCheck, Loader2,
  Zap, Trash2, FileText, ExternalLink, Upload, ChevronLeft, ChevronRight, Download,
  TrendingUp, BarChart2, Mail, Plus, Edit2, Eye, EyeOff, KeyRound, Building2, Clock,
  Unlock, AlertCircle, ImageIcon, MapPin, PhoneOff, RotateCcw, Share2, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TourEditor } from "./TourEditor";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const MID    = "#1C1C1E";
const API    = process.env.REACT_APP_BACKEND_URL;

/** Small carrier logo thumbnail — falls back to 2-letter initials if no logo uploaded */
function CarrierLogoThumb({ carrierKey, name }) {
  const [broken, setBroken] = useState(false);
  // carrierKey must match carrier_profiles.carrier_key (lowercase, spaces preserved)
  const logoUrl = `${API}/api/market/carrier/logo/${encodeURIComponent(carrierKey)}`;
  const abbr    = (name || "").split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
  if (broken) {
    return (
      <div
        data-testid={`carrier-logo-thumb-${carrierKey}`}
        className="w-7 h-7 shrink-0 flex items-center justify-center font-head text-[10px] font-black"
        style={{ background: "rgba(255,77,0,0.1)", border: "1px solid rgba(255,77,0,0.25)", color: ACCENT }}>
        {abbr}
      </div>
    );
  }
  return (
    <img
      src={logoUrl}
      alt={name}
      data-testid={`carrier-logo-thumb-${carrierKey}`}
      className="w-7 h-7 shrink-0 object-contain"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
      onError={() => setBroken(true)}
    />
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function AdminPanel({ user, token, drivers, bids, onCropFace, onBidStatusChange, onSeedDemo, onSeedFullMarket, onLoadRealDrivers, onEnableAllConsent, onCropAllFaces, onTransferDriver, onRefresh }) {
  const [changingHub, setChangingHub] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [newUn, setNewUn] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [bonusRequests, setBonusRequests] = useState([]);
  const [approvingBonus, setApprovingBonus] = useState(null);
  const [returningToMarket, setReturningToMarket] = useState(null);
  // Annul Agreement per-row state
  const [annullingId,   setAnnullingId]   = useState(null); // driver id showing inline form
  const [annulBidId,    setAnnulBidId]    = useState(null); // matching bid id
  const [annulReason,   setAnnulReason]   = useState("");
  const [annulNotes,    setAnnulNotes]    = useState("");
  const [annulSending,  setAnnulSending]  = useState(false);
  const [pendingReferrals, setPendingReferrals] = useState([]);
  const [markingReferralPaid, setMarkingReferralPaid] = useState(null);

  // ── Annulment Table state ──
  const [annulmentsData, setAnnulmentsData]   = useState(null);  // {annulments, total}
  const [annulTableSort, setAnnulTableSort]   = useState({ by: "created_at", dir: -1 });
  const [annulTablePage, setAnnulTablePage]   = useState(1);
  const [annulTableLoading, setAnnulTableLoading] = useState(false);
  const [csvExporting, setCsvExporting]       = useState(false);

  useEffect(() => {
    setAnnulTableLoading(true);
    api.marketAdminGetAnnulments(token, annulTablePage, annulTableSort.by, annulTableSort.dir)
      .then(r => setAnnulmentsData(r))
      .catch(() => {})
      .finally(() => setAnnulTableLoading(false));
  }, [token, annulTablePage, annulTableSort]);

  async function handleExportCSV() {
    setCsvExporting(true);
    try {
      const blob = await api.marketAdminExportAnnulmentsCSV(token);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "annulments.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed."); }
    finally { setCsvExporting(false); }
  }

  function handleAnnulSort(col) {
    setAnnulTableSort(prev => prev.by === col ? { by: col, dir: prev.dir * -1 } : { by: col, dir: -1 });
    setAnnulTablePage(1);
  }

  // ── IndexNow Re-Ping state ──
  const [indexNowLoading, setIndexNowLoading] = useState(false);
  const [indexNowResult, setIndexNowResult]   = useState(null);

  // ── Manual Transfer state ──
  const [showManualTransfer, setShowManualTransfer] = useState(false);
  const [mtDriverId,   setMtDriverId]   = useState("");
  const [mtCarrier,    setMtCarrier]    = useState("");
  const [mtNotes,      setMtNotes]      = useState("");
  const [mtFee,        setMtFee]        = useState("");
  const [mtLoading,    setMtLoading]    = useState(false);

  // ── Counter-Offer state ──
  const [counterOfferBidId, setCounterOfferBidId] = useState(null);
  const [counterOfferFee, setCounterOfferFee]     = useState("");
  const [counterOfferSending, setCounterOfferSending] = useState(false);
  // ── Bid row expand / decline state ──
  const [expandedBidIds, setExpandedBidIds] = useState(new Set());
  const [decliningBidId, setDecliningBidId] = useState(null);
  const toggleBidExpanded = (id) => setExpandedBidIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Carrier profile map: carrier_key → {display_name, logo_path} ──
  const [carrierProfileMap, setCarrierProfileMap] = useState({});

  useEffect(() => {
    api.marketGetCarrierProfiles(token)
      .then((res) => {
        const map = {};
        (res.profiles || []).forEach((p) => {
          if (p.carrier_key) map[p.carrier_key] = p;
        });
        setCarrierProfileMap(map);
      })
      .catch(() => {});
  }, [token]);


  async function handleReturnToMarket(appId, driverName) {
    if (!window.confirm(`Return "${driverName}" to the live market? This will clear ALL their bids and they will start fresh.`)) return;
    setReturningToMarket(appId);
    try {
      await api.marketReturnToMarket(token, appId);
      toast.success(`${driverName} is back on the market — all bids cleared.`);
      onRefresh?.();
    } catch { toast.error("Failed to return driver to market."); }
    finally { setReturningToMarket(null); }
  }

  async function handleAnnulConfirm() {
    if (!annulBidId || !annulReason) return;
    setAnnulSending(true);
    try {
      await api.marketAnnulTransfer(token, annulBidId, annulReason, annulNotes);
      toast.success("Agreement annulled — driver returned to available, carrier notified.");
      setAnnullingId(null); setAnnulBidId(null); setAnnulReason(""); setAnnulNotes("");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to annul agreement.");
    } finally { setAnnulSending(false); }
  }

  // Load pending referrals on mount
  useEffect(() => {
    api.marketAdminPendingReferrals(token).then(r => setPendingReferrals(r.referrals || [])).catch(() => {});
  }, [token]);

  async function handleMarkReferralPaid(referredAppId, referredName, referrerName) {
    if (!window.confirm(`Mark $400 referral bonus as PAID to ${referrerName} for referring ${referredName}?`)) return;
    setMarkingReferralPaid(referredAppId);
    try {
      await api.marketAdminMarkReferralPaid(token, referredAppId);
      toast.success(`$400 referral bonus marked as paid to ${referrerName}.`);
      setPendingReferrals(prev => prev.map(r => r.id === referredAppId ? { ...r, referral_bonus_paid: true } : r));
    } catch { toast.error("Failed to mark referral as paid."); }
    finally { setMarkingReferralPaid(null); }
  }


  async function handleSubmitCounterOffer(bidId) {
    const fee = parseInt(counterOfferFee, 10);
    if (!fee || fee < 100) { toast.error("Enter a valid fee ($100 minimum)"); return; }
    setCounterOfferSending(true);
    try {
      await api.marketCounterOffer(token, bidId, fee);
      toast.success(`Counter-offer of $${fee.toLocaleString()} sent to carrier`);
      setCounterOfferBidId(null);
      setCounterOfferFee("");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send counter-offer");
    } finally {
      setCounterOfferSending(false);
    }
  }

  async function handleSubmitIndexNow() {
    setIndexNowLoading(true);
    setIndexNowResult(null);
    try {
      const res = await api.marketAdminSubmitIndexNow(token);
      setIndexNowResult({ ok: true, submitted: res.submitted, skipped: res.skipped });
      if (res.submitted === 0) {
        toast.success(`All ${res.skipped} pages already indexed — nothing new to submit`);
      } else {
        toast.success(`IndexNow pinged — ${res.submitted} new pages submitted (${res.skipped} already indexed)`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "IndexNow failed";
      setIndexNowResult({ ok: false, msg });
      toast.error(msg);
    } finally { setIndexNowLoading(false); }
  }

  async function handleManualTransfer() {
    if (!mtDriverId) { toast.error("Select a driver"); return; }
    if (!mtCarrier.trim()) { toast.error("Enter carrier name"); return; }
    setMtLoading(true);
    try {
      const res = await api.marketAdminManualTransfer(token, mtDriverId, mtCarrier.trim(), mtNotes.trim(), mtFee ? parseInt(mtFee, 10) : 0);
      toast.success(`${res.driver_name} manually transferred to ${res.carrier_name}`);
      setShowManualTransfer(false);
      setMtDriverId(""); setMtCarrier(""); setMtNotes(""); setMtFee("");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Manual transfer failed");
    } finally { setMtLoading(false); }
  }

  // ── Fake Bid Injector state ──
  const [showFakeBidPanel, setShowFakeBidPanel] = useState(false);
  const [fakeBidCount, setFakeBidCount]         = useState(0);
  const [fakeBidForm, setFakeBidForm]           = useState({
    driver_id: "", driver_name: "", carrier_name: "", position_type: "semi_truck",
    equipment_package: "Dry Van", pay_program: "company_driver",
    driver_pay_cpm: "0.75", driver_pay_percentage: "", placement_fee: "1500",
    payment_option: "standard",
  });
  const [injectingBid, setInjectingBid]   = useState(false);
  const [clearingBids, setClearingBids]   = useState(false);

  // ── Carrier Win Rate Stats state ──
  const [showCarrierStats, setShowCarrierStats] = useState(false);
  const [carrierStats, setCarrierStats]         = useState(null);
  const [loadingStats, setLoadingStats]         = useState(false);

  const FAKE_CARRIER_NAMES = [
    "Apex Logistics LLC", "Prime Freight Holdings", "Crossroads Transport Co",
    "Keystone Carriers Inc", "Summit Trucking Group", "Atlas Fleet Services",
    "BlueStar Freight LLC", "Tri-State Haulers Inc", "Midwest Express Carriers",
    "Heartland Transport LLC", "Freedom Road Logistics", "Iron Eagle Freight",
    "Cascade Trucking Co", "Pioneer Transport Group", "Titan Freight Solutions",
  ];

  async function fetchFakeBidCount() {
    try {
      const res = await api.marketAdminFakeBidCount(token);
      setFakeBidCount(res.count || 0);
    } catch { /* ignore */ }
  }

  async function fetchCarrierStats() {
    setLoadingStats(true);
    try {
      const res = await api.marketAdminCarrierStats(token);
      setCarrierStats(res);
    } catch { toast.error("Failed to load carrier stats"); }
    finally { setLoadingStats(false); }
  }

  async function handleInjectFakeBid() {
    if (!fakeBidForm.driver_id) { toast.error("Select a driver first"); return; }
    if (!fakeBidForm.carrier_name) { toast.error("Enter a carrier name"); return; }
    setInjectingBid(true);
    try {
      const isCpm = fakeBidForm.pay_program === "company_driver";
      await api.marketAdminInjectFakeBid(token, {
        ...fakeBidForm,
        placement_fee:        parseInt(fakeBidForm.placement_fee, 10),
        driver_pay_cpm:       isCpm ? parseFloat(fakeBidForm.driver_pay_cpm) : null,
        driver_pay_percentage:!isCpm ? parseFloat(fakeBidForm.driver_pay_percentage) : null,
        driver_pay_package:   fakeBidForm.equipment_package,
      });
      toast.success(`Fake bid injected for ${fakeBidForm.driver_name}`);
      await fetchFakeBidCount();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to inject bid");
    } finally { setInjectingBid(false); }
  }

  async function handleAutoGenerateBids() {
    const liveDrvs = (drivers || []).filter((d) => d.first_name && d.id);
    if (liveDrvs.length === 0) { toast.error("No drivers on board"); return; }
    setInjectingBid(true);
    const EQUIP_POOLS = {
      semi_truck: ["Dry Van","Reefer","Flatbed","Dry Van","Reefer"],
      box_truck:  ["26ft Box","Sprinter/Cargo Van","26ft Box"],
    };
    const PROG_POOL = [
      { pay_program:"company_driver", driver_pay_cpm:0.75, driver_pay_percentage:null },
      { pay_program:"company_driver", driver_pay_cpm:0.78, driver_pay_percentage:null },
      { pay_program:"rental",         driver_pay_cpm:null,  driver_pay_percentage:87 },
      { pay_program:"lease",          driver_pay_cpm:null,  driver_pay_percentage:86 },
      { pay_program:"owner_operator", driver_pay_cpm:null,  driver_pay_percentage:89 },
    ];
    const FEE_FLOOR = { company_driver:1500, rental:1700, lease:1900, owner_operator:2100 };
    const BT_FLOOR  = { company_driver:800,  rental:1000, lease:1200, owner_operator:1400 };
    const shuffled  = [...liveDrvs].sort(() => Math.random() - 0.5);
    const targets   = shuffled.slice(0, Math.min(5, shuffled.length));
    let injected = 0;
    for (const drv of targets) {
      const posType = Math.random() > 0.25 ? "semi_truck" : "box_truck";
      const equipList = EQUIP_POOLS[posType];
      const equip = equipList[Math.floor(Math.random() * equipList.length)];
      const prog = PROG_POOL[Math.floor(Math.random() * PROG_POOL.length)];
      const floor = (posType === "semi_truck" ? FEE_FLOOR : BT_FLOOR)[prog.pay_program];
      const fee = (Math.floor(floor / 100) + Math.floor(Math.random() * 2)) * 100;
      const carrier = FAKE_CARRIER_NAMES[Math.floor(Math.random() * FAKE_CARRIER_NAMES.length)];
      try {
        await api.marketAdminInjectFakeBid(token, {
          driver_id: drv.id, driver_name: `${drv.first_name} ${drv.last_name}`,
          carrier_name: carrier, carrier_phone: "7087771234",
          position_type: posType, equipment_package: equip,
          pay_program: prog.pay_program, driver_pay_cpm: prog.driver_pay_cpm,
          driver_pay_percentage: prog.driver_pay_percentage,
          driver_pay_package: equip, placement_fee: fee, payment_option: "standard",
        });
        injected++;
      } catch { /* skip individual failures */ }
    }
    toast.success(`Auto-generated ${injected} fake bids`);
    await fetchFakeBidCount();
    setInjectingBid(false);
  }

  async function handleClearFakeBids() {
    if (!window.confirm(`Clear all ${fakeBidCount} fake bids from the Live Bid Board?`)) return;
    setClearingBids(true);
    try {
      const res = await api.marketAdminClearFakeBids(token);
      toast.success(`Cleared ${res.deleted_count} fake bids`);
      setFakeBidCount(0);
    } catch { toast.error("Failed to clear fake bids"); }
    finally { setClearingBids(false); }
  }

  // ── Moderation Alerts state ──
  const [modAlerts, setModAlerts] = useState([]);
  const [modUnread, setModUnread] = useState(0);
  const [modBans, setModBans] = useState([]);
  const [showModPanel, setShowModPanel] = useState(false);

  // ── Admin Doc Manager state ──
  const [adminDocs, setAdminDocs] = useState([]);
  const [adminDocLoading, setAdminDocLoading] = useState(false);
  const [adminDocSearch, setAdminDocSearch] = useState("");
  const [adminDocExpanded, setAdminDocExpanded] = useState(new Set());
  const [adminDocUploading, setAdminDocUploading] = useState({});
  const [adminDocRemoving, setAdminDocRemoving] = useState({});
  const adminDocFileRefs = useRef({});

  // ── Driver Phone Directory state ──
  const [phoneDir, setPhoneDir] = useState([]);
  const [phoneDirSearch, setPhoneDirSearch] = useState("");
  const [showPhoneDir, setShowPhoneDir] = useState(false);

  // ── Hub Credentials state ──
  const [hubCreds, setHubCreds] = useState([]);
  const [showHubCreds, setShowHubCreds] = useState(false);
  const [showPassFor, setShowPassFor] = useState({});

  // ── Win History & Self-Apply state ──
  const [winHistory, setWinHistory] = useState([]);
  const [showWinHistory, setShowWinHistory] = useState(false);
  const [winHistorySearch, setWinHistorySearch] = useState("");
  const [selfApplyPending, setSelfApplyPending] = useState([]);
  const [showSelfApply, setShowSelfApply] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  // ── Transfer Board search + pagination ──
  const [tbSearch, setTbSearch] = useState("");
  const [tbPage, setTbPage] = useState(0);
  const TB_PAGE_SIZE = 2;

  const loadAdminDocs = useCallback(async () => {
    setAdminDocLoading(true);
    try {
      const data = await api.marketAdminGetDriversWithDocs(token);
      setAdminDocs(data);
    } catch { /* ignore */ } finally { setAdminDocLoading(false); }
  }, [token]);

  const ADMIN_DOC_TYPES = [
    { key: "cdl",            label: "CDL Front",      pathKey: "cdl_path" },
    { key: "cdl_back",       label: "CDL Back",       pathKey: "cdl_back_path" },
    { key: "previous_cdl_1", label: "Prev CDL 1",     pathKey: "previous_cdl_1_path" },
    { key: "previous_cdl_2", label: "Prev CDL 2",     pathKey: "previous_cdl_2_path" },
    { key: "medical",        label: "Medical",        pathKey: "medical_path" },
    { key: "mvr",            label: "MVR",            pathKey: "mvr_path" },
    { key: "psp",            label: "PSP",            pathKey: "psp_path" },
    { key: "clearinghouse",  label: "Clearinghouse",  pathKey: "clearinghouse_path" },
  ];

  async function handleAdminDocUpload(driverId, docType, file) {
    const uploadKey = `${driverId}-${docType}`;
    setAdminDocUploading((p) => ({ ...p, [uploadKey]: true }));
    try {
      const { path: file_path } = await api.uploadFile(file);
      await api.marketAdminUploadDriverDoc(token, driverId, docType, file_path);
      setAdminDocs((prev) =>
        prev.map((d) => {
          if (d.id !== driverId) return d;
          const pathKey = ADMIN_DOC_TYPES.find((t) => t.key === docType)?.pathKey;
          return pathKey ? { ...d, [pathKey]: file_path } : d;
        })
      );
      toast.success(`${docType.toUpperCase()} uploaded`);
    } catch { toast.error("Upload failed"); } finally {
      setAdminDocUploading((p) => ({ ...p, [uploadKey]: false }));
    }
  }

  async function handleAdminDocRemove(driverId, docType) {
    const removeKey = `${driverId}-${docType}`;
    setAdminDocRemoving((p) => ({ ...p, [removeKey]: true }));
    try {
      await api.marketAdminRemoveDriverDoc(token, driverId, docType);
      setAdminDocs((prev) =>
        prev.map((d) => {
          if (d.id !== driverId) return d;
          const pathKey = ADMIN_DOC_TYPES.find((t) => t.key === docType)?.pathKey;
          return pathKey ? { ...d, [pathKey]: "" } : d;
        })
      );
      toast.success(`${docType.toUpperCase()} removed`);
    } catch { toast.error("Remove failed"); } finally {
      setAdminDocRemoving((p) => ({ ...p, [removeKey]: false }));
    }
  }

  async function handleAdminDocView(path) {
    try {
      const blob = await api.marketAdminViewFile(token, path);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Could not open file"); }
  }

  async function handleAdminDQBundle(driverId, driverName) {
    try {
      toast.loading("Generating DQ Bundle…", { id: `bundle-${driverId}` });
      const blob = await api.marketDownloadDQBundle(token, driverId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hylas-dq-bundle-${(driverName || "driver").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DQ Bundle downloaded", { id: `bundle-${driverId}` });
    } catch { toast.error("Failed to generate bundle", { id: `bundle-${driverId}` }); }
  }

  async function handleAdminOptOut(driverId, type) {
    try {
      await api.marketLogOptOut(token, driverId, type);
      setAdminDocs((prev) => prev.map((d) => d.id === driverId ? { ...d, contact_opted_out: true, opt_out_type: type } : d));
      toast.success(type === "sms_stop" ? "STOP logged — automated texts blocked" : "Call opt-out logged — automated emails blocked");
    } catch { toast.error("Could not log opt-out"); }
  }

  async function handleAdminRevokeOptOut(driverId) {
    try {
      await api.marketRevokeOptOut(token, driverId);
      setAdminDocs((prev) => prev.map((d) => d.id === driverId ? { ...d, contact_opted_out: false, opt_out_type: "" } : d));
      toast.success("Contact opt-out removed");
    } catch { toast.error("Could not remove opt-out"); }
  }

  const loadBonusRequests = useCallback(async () => {
    try {
      const data = await api.marketAdminGetBonusRequests(token);
      setBonusRequests(data);
    } catch { /* ignore */ }
  }, [token]);

  const loadModAlerts = useCallback(async () => {
    try {
      const res = await api.marketGetModAlerts(token);
      setModAlerts(res.alerts || []);
      setModUnread(res.unread_count || 0);
    } catch { /* ignore */ }
  }, [token]);

  const loadBans = useCallback(async () => {
    try {
      const bns = await api.marketListBans(token);
      setModBans(Array.isArray(bns) ? bns : []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    loadBonusRequests(); loadAdminDocs(); loadModAlerts(); loadBans(); fetchFakeBidCount();
    api.marketGetDriversFull(token).then(setPhoneDir).catch(() => {});
    api.marketGetHubCredentials(token).then((r) => setHubCreds(r.hubs || [])).catch(() => {});
    api.marketGetWinHistory(token).then((r) => setWinHistory(r.history || [])).catch(() => {});
    api.marketAdminGetSelfApplyPending(token).then((r) => setSelfApplyPending(r.pending || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBonusRequests, loadAdminDocs, loadModAlerts, loadBans, token]);

  async function markAlertRead(id) {
    try { await api.marketMarkAlertRead(token, id); loadModAlerts(); } catch { /* ignore */ }
  }

  async function unbanUser(banId) {
    try { await api.marketUnban(token, banId); toast.success("User unbanned"); loadBans(); } catch { toast.error("Failed to unban"); }
  }

  async function approveBonus(appId, bonusType, extraBody = {}) {
    setApprovingBonus(`${appId}-${bonusType}`);
    try {
      await api.marketAdminApproveBonus(token, appId, bonusType, extraBody);
      toast.success("Bonus approved");
      await loadBonusRequests(); // await so state refreshes before spinner clears
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve bonus");
    } finally {
      setApprovingBonus(null);
    }
  }

  // Map approve-type → reset-field for the bonus-reset endpoint
  const BONUS_RESET_FIELD = {
    docs:            "docs",
    drug_screen:     "drug_screen",
    start:           "start_date",
    carrier_start:   "carrier_start",
    carrier_week2:   "carrier_week2",
    carrier_onetime: "carrier_onetime",
    carrier_signup:  "signup",
  };

  async function unapproveBonus(appId, bonusType) {
    const field = BONUS_RESET_FIELD[bonusType];
    if (!field) return;
    setApprovingBonus(`${appId}-${bonusType}-revoke`);
    try {
      await api.marketAdminBonusReset(token, appId, field);
      toast.success("Bonus revoked — set back to pending");
      await loadBonusRequests(); // await so state refreshes before spinner clears (prevents stale-state flash)
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke bonus");
    } finally {
      setApprovingBonus(null);
    }
  }

  async function reviewSelfApply(appId, action) {
    setReviewingId(appId);
    try {
      await api.marketAdminReviewSelfApply(token, appId, action);
      toast.success(action === "approve" ? "Driver approved and visible on Market." : "Application rejected.");
      setSelfApplyPending((prev) => prev.filter((p) => p.id !== appId));
    } catch { toast.error("Action failed."); } finally { setReviewingId(null); }
  }

  async function changePassword(hub) {
    if (!newPw && !newUn) { setPwMsg("Enter a new username and/or password."); return; }
    if (newPw && newPw.length < 8) { setPwMsg("Password must be at least 8 characters."); return; }
    setSaving(true);
    setPwMsg("");
    try {
      await api.marketChangePassword(token, hub, newPw || undefined, newUn || undefined);
      toast.success(`${hub} credentials updated`);
      setNewPw("");
      setNewUn("");
      setChangingHub(null);
    } catch { setPwMsg("Failed."); } finally { setSaving(false); }
  }

  const allHubs = ["admin", "carrier", "recruiter", "safety", "driver"];
  const liveBids = bids.filter((b) => b.status === "live");
  const transferred = drivers.filter((d) => d.market_status === "transferred");

  // Group live bids by driver
  const bidsByDriver = {};
  liveBids.forEach((b) => {
    if (!bidsByDriver[b.driver_id]) bidsByDriver[b.driver_id] = [];
    bidsByDriver[b.driver_id].push(b);
  });

  // Transfer Board: filter + paginate by driver name
  const tbDriverEntries = Object.entries(bidsByDriver).filter(([dId, dBids]) => {
    if (!tbSearch.trim()) return true;
    const q = tbSearch.toLowerCase();
    const name = (dBids[0]?.driver_name || "").toLowerCase();
    const driver = drivers.find((d) => d.id === dId);
    return name.includes(q) || (driver?.state || "").toLowerCase().includes(q);
  });
  const tbTotalPages = Math.ceil(tbDriverEntries.length / TB_PAGE_SIZE);
  const tbPaged = tbDriverEntries.slice(tbPage * TB_PAGE_SIZE, tbPage * TB_PAGE_SIZE + TB_PAGE_SIZE);

  return (
    <>
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Drivers on Market", v: drivers.length, c: ACCENT },
          { label: "Live Bids", v: liveBids.length, c: "#38BDF8" },
          { label: "Transferred", v: transferred.length, c: GOLD },
          { label: "Total Bids", v: bids.length, c: "#888" },
        ].map((s) => (
          <div key={s.label} className="p-4" style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="text-[9px] text-white/40 uppercase tracking-[0.15em] font-head mb-1">{s.label}</p>
            <p className="font-head font-black text-xl" style={{ color: s.c }}>{s.v}</p>
          </div>
        ))}
      </div>

      {/* Driver Phone Directory */}
      <div style={{ background: MID, border: "1px solid rgba(245,200,66,0.18)" }}>
        <button data-testid="admin-phone-dir-toggle" onClick={() => setShowPhoneDir((v) => !v)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showPhoneDir ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <Users className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Driver Phone Directory</p>
            <p className="text-[10px] text-white/30 mt-0.5">Phone numbers for all market drivers — Admin & Recruiter only</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
            {phoneDir.length} drivers
          </span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showPhoneDir ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showPhoneDir && (
          <>
            <div className="px-4 pt-3 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
                <input type="text" value={phoneDirSearch} onChange={(e) => setPhoneDirSearch(e.target.value)}
                  placeholder="Search driver name…" data-testid="admin-phone-dir-search"
                  className="w-full bg-[#0A0A0A] border border-white/08 text-white pl-8 pr-3 py-2 text-xs focus:outline-none focus:border-[#F5C842] placeholder:text-white/20" />
              </div>
            </div>
            <div className="divide-y divide-white/05 max-h-72 overflow-y-auto">
              {phoneDir.filter((d) => `${d.first_name} ${d.last_name}`.toLowerCase().includes(phoneDirSearch.toLowerCase()))
                .map((d) => (
                  <div key={d.id} className="px-4 py-3 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-head text-sm font-bold text-white truncate">{d.first_name} {d.last_name}</p>
                        {d.contact_opted_out && (
                          <span data-testid={`admin-optout-badge-${d.id}`} className="font-head text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5 shrink-0"
                            style={{ background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}>
                            OPT-OUT
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-white/40 mt-0.5">{d.state} · {d.cdl_class || "CDL"} · {d.market_status || "applied"}</p>
                    </div>
                    <a href={`tel:${d.phone}`} data-testid={`admin-driver-phone-${d.id}`}
                      className="font-mono text-sm font-bold shrink-0 transition-colors hover:brightness-125" style={{ color: d.contact_opted_out ? "#555" : GOLD }}>
                      {d.phone || "—"}
                    </a>
                  </div>
                ))}
              {phoneDir.filter((d) => `${d.first_name} ${d.last_name}`.toLowerCase().includes(phoneDirSearch.toLowerCase())).length === 0 && (
                <p className="px-4 py-4 text-xs text-white/25 text-center">No drivers found.</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hub Credentials (read-only view) */}
      <div style={{ background: MID, border: "1px solid rgba(167,139,250,0.18)" }}>
        <button data-testid="admin-hub-creds-toggle" onClick={() => setShowHubCreds((v) => !v)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showHubCreds ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <Lock className="w-4 h-4 shrink-0" style={{ color: "#A78BFA" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Hub Credentials</p>
            <p className="text-[10px] text-white/30 mt-0.5">All 5 market hub login credentials — Admin only</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(167,139,250,0.1)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.25)" }}>SECURE</span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showHubCreds ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showHubCreds && (
          <div className="divide-y divide-white/05">
            {hubCreds.map((h) => (
              <div key={h.hub} className="px-4 py-3 flex items-center gap-4 flex-wrap">
                <div className="w-20 shrink-0">
                  <span className="font-head text-[10px] uppercase tracking-[0.1em] px-2 py-0.5"
                    style={{ background: "rgba(167,139,250,0.1)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.2)" }}>{h.hub}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-head text-xs font-bold text-white/80 mb-0.5">{h.label}</p>
                  <p className="text-[11px] text-white/50 font-mono">User: <span className="text-white/80">{h.username}</span></p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <p className="text-[11px] text-white/50 font-mono">Pass: <span className="text-white/80">{showPassFor[h.hub] ? h.password : "••••••••••"}</span></p>
                    <button onClick={() => setShowPassFor((prev) => ({ ...prev, [h.hub]: !prev[h.hub] }))}
                      className="text-white/25 hover:text-white/60 transition-colors" data-testid={`admin-show-pass-${h.hub}`}>
                      <span className="text-[9px]">{showPassFor[h.hub] ? "hide" : "show"}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Market Actions */}
      <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Market Bulk Actions</p>
        </div>
        <div className="px-4 py-4 grid sm:grid-cols-3 gap-3">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={onLoadRealDrivers}
            data-testid="admin-load-real-drivers"
            className="flex flex-col items-start px-4 py-3 border transition-colors group"
            style={{ borderColor: "rgba(255,77,0,0.6)", background: "rgba(255,77,0,0.08)" }}>
            <Flame className="w-5 h-5 mb-2" style={{ color: ACCENT }} />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">Load Real Drivers</p>
            <p className="text-[10px] text-white/30 mt-0.5">Removes demo drivers · Puts all 20 real CSV applications on market</p>
          </motion.button>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={onSeedFullMarket}
            data-testid="admin-seed-full-market"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-white/30 transition-colors group">
            <Star className="w-5 h-5 mb-2 text-white/40 group-hover:text-white/70" />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white/70">Seed Full Market</p>
            <p className="text-[10px] text-white/25 mt-0.5">Enable all real DB apps + add bids</p>
          </motion.button>
          <button onClick={onSeedDemo} data-testid="admin-seed-demo"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-[#4ADE80] transition-colors group">
            <Truck className="w-5 h-5 mb-2 group-hover:text-[#4ADE80] text-white/40 transition-colors" />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">Seed Demo Drivers</p>
            <p className="text-[10px] text-white/30 mt-0.5">Add 3 demo driver cards to the market</p>
          </button>
          <button onClick={onEnableAllConsent} data-testid="admin-enable-consent"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-[#38BDF8] transition-colors group">
            <CheckCircle2 className="w-5 h-5 mb-2 group-hover:text-[#38BDF8] text-white/40 transition-colors" />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">Enable All Consent</p>
            <p className="text-[10px] text-white/30 mt-0.5">Push all existing applications to market</p>
          </button>
          <button onClick={onCropAllFaces} data-testid="admin-crop-all"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-[#F5C842] transition-colors group">
            <FileCheck className="w-5 h-5 mb-2 group-hover:text-[#F5C842] text-white/40 transition-colors" />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">Crop All CDL Faces</p>
            <p className="text-[10px] text-white/30 mt-0.5">Run face crop for all drivers with CDL</p>
          </button>
          {/* IndexNow Re-Ping */}
          <button onClick={handleSubmitIndexNow} disabled={indexNowLoading} data-testid="admin-submit-indexnow"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-[#38BDF8] transition-colors group disabled:opacity-50">
            <TrendingUp className="w-5 h-5 mb-2 group-hover:text-[#38BDF8] text-white/40 transition-colors" style={{ color: indexNowResult?.ok ? "#38BDF8" : undefined }} />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">IndexNow Re-Ping</p>
            <p className="text-[10px] text-white/30 mt-0.5">
              {indexNowLoading ? "Submitting new URLs…" :
               indexNowResult?.ok ? (indexNowResult.submitted === 0 ? "All pages already indexed" : `Done — ${indexNowResult.submitted} new · ${indexNowResult.skipped} already indexed`) :
               "Submit only NEW unindexed pages to Bing/Yandex"}
            </p>
          </button>
          {/* Manual Transfer */}
          <button onClick={() => setShowManualTransfer(true)} data-testid="admin-manual-transfer-btn"
            className="flex flex-col items-start px-4 py-3 border border-white/10 hover:border-[#22C55E] transition-colors group">
            <ArrowRight className="w-5 h-5 mb-2 group-hover:text-[#22C55E] text-white/40 transition-colors" />
            <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">Manual Transfer</p>
            <p className="text-[10px] text-white/30 mt-0.5">Transfer driver to carrier without a bid (emergency fix)</p>
          </button>
        </div>
      </div>

      {/* ── Fake Bid Injector ── */}
      <div style={{ background: MID, border: "1px solid rgba(255,77,0,0.22)" }}>
        <button data-testid="admin-fake-bid-toggle"
          onClick={() => { setShowFakeBidPanel((v) => !v); if (!showFakeBidPanel) fetchFakeBidCount(); }}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showFakeBidPanel ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <Flame className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Fake Bid Injector</p>
            <p className="text-[10px] text-white/30 mt-0.5">Inject marketing bids onto the Live Bid Board — visible to all carrier hubs</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(255,77,0,0.1)", color: ACCENT, border: "1px solid rgba(255,77,0,0.3)" }}>
            {fakeBidCount} active
          </span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showFakeBidPanel ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showFakeBidPanel && (
          <div className="px-4 py-4 space-y-4">
            <div>
              <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Driver</label>
              <select data-testid="fake-bid-driver-select" value={fakeBidForm.driver_id}
                onChange={(e) => {
                  const drv = drivers.find((d) => d.id === e.target.value);
                  setFakeBidForm((p) => ({ ...p, driver_id: e.target.value, driver_name: drv ? `${drv.first_name} ${drv.last_name}` : "" }));
                }}
                className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]">
                <option value="">— Select driver —</option>
                {(drivers || []).map((d) => (<option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Carrier Name</label>
                <input data-testid="fake-bid-carrier-name" type="text" value={fakeBidForm.carrier_name}
                  onChange={(e) => setFakeBidForm((p) => ({ ...p, carrier_name: e.target.value }))}
                  placeholder="e.g. Apex Logistics LLC"
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00] placeholder:text-white/20" />
              </div>
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Placement Fee ($)</label>
                <input data-testid="fake-bid-placement-fee" type="number" value={fakeBidForm.placement_fee}
                  onChange={(e) => setFakeBidForm((p) => ({ ...p, placement_fee: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Position Type</label>
                <select data-testid="fake-bid-position-type" value={fakeBidForm.position_type}
                  onChange={(e) => setFakeBidForm((p) => ({ ...p, position_type: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]">
                  <option value="semi_truck">Semi Truck</option>
                  <option value="box_truck">Box Truck</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Equipment</label>
                <select data-testid="fake-bid-equipment" value={fakeBidForm.equipment_package}
                  onChange={(e) => setFakeBidForm((p) => ({ ...p, equipment_package: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]">
                  {fakeBidForm.position_type === "semi_truck"
                    ? ["Dry Van","Reefer","Flatbed","Step Deck","Tanker"].map((eq) => <option key={eq}>{eq}</option>)
                    : ["26ft Box","Sprinter/Cargo Van","20ft Box"].map((eq) => <option key={eq}>{eq}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Pay Program</label>
                <select data-testid="fake-bid-pay-program" value={fakeBidForm.pay_program}
                  onChange={(e) => setFakeBidForm((p) => ({ ...p, pay_program: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]">
                  <option value="company_driver">Company Driver</option>
                  <option value="rental">Rental</option>
                  <option value="lease">Lease</option>
                  <option value="owner_operator">Owner-Operator</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">
                  {fakeBidForm.pay_program === "company_driver" ? "CPM Rate ($/mi)" : "Driver % Cut"}
                </label>
                <input data-testid="fake-bid-pay-value" type="number"
                  step={fakeBidForm.pay_program === "company_driver" ? "0.01" : "1"}
                  value={fakeBidForm.pay_program === "company_driver" ? fakeBidForm.driver_pay_cpm : fakeBidForm.driver_pay_percentage}
                  onChange={(e) => setFakeBidForm((p) => ({
                    ...p,
                    [fakeBidForm.pay_program === "company_driver" ? "driver_pay_cpm" : "driver_pay_percentage"]: e.target.value,
                  }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-xs focus:outline-none focus:border-[#FF4D00]" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid="admin-inject-fake-bid-btn" onClick={handleInjectFakeBid} disabled={injectingBid}
                className="flex items-center gap-2 px-4 py-2 text-xs font-head font-bold uppercase tracking-[0.12em] text-black disabled:opacity-50 transition-opacity"
                style={{ background: ACCENT }}>
                {injectingBid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Inject Bid
              </button>
              <button data-testid="admin-auto-generate-bids-btn" onClick={handleAutoGenerateBids} disabled={injectingBid}
                className="flex items-center gap-2 px-4 py-2 text-xs font-head font-bold uppercase tracking-[0.12em] disabled:opacity-50 transition-opacity"
                style={{ background: "rgba(255,77,0,0.15)", color: ACCENT, border: "1px solid rgba(255,77,0,0.35)" }}>
                <Star className="w-3.5 h-3.5" />
                Auto-Generate 5 Bids
              </button>
              {fakeBidCount > 0 && (
                <button data-testid="admin-clear-fake-bids-btn" onClick={handleClearFakeBids} disabled={clearingBids}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-head font-bold uppercase tracking-[0.12em] disabled:opacity-50 transition-opacity"
                  style={{ background: "rgba(255,255,255,0.04)", color: "#ff6b6b", border: "1px solid rgba(255,107,107,0.25)" }}>
                  {clearingBids ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Clear {fakeBidCount} Fake Bids
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Carrier Win Rate Stats ── */}
      <div style={{ background: MID, border: "1px solid rgba(56,189,248,0.22)" }}>
        <button data-testid="admin-carrier-stats-toggle"
          onClick={() => { setShowCarrierStats((v) => !v); if (!showCarrierStats && !carrierStats) fetchCarrierStats(); }}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showCarrierStats ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <BarChart2 className="w-4 h-4 shrink-0" style={{ color: "#38BDF8" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Carrier Win Rate Stats</p>
            <p className="text-[10px] text-white/30 mt-0.5">Bids placed, wins, win rate % and avg placement fee per carrier</p>
          </div>
          {carrierStats && (
            <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
              style={{ background: "rgba(56,189,248,0.1)", color: "#38BDF8", border: "1px solid rgba(56,189,248,0.25)" }}>
              {carrierStats.leaderboard?.length ?? 0} carriers
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showCarrierStats ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showCarrierStats && (
          <div className="px-4 pb-4 pt-3 space-y-4">
            {loadingStats ? (
              <div className="flex items-center gap-2 justify-center py-6 text-white/30 text-xs">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
              </div>
            ) : !carrierStats ? (
              <p className="text-xs text-white/30 text-center py-4">No data yet.</p>
            ) : (
              <>
                {/* Summary pills */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Total Bids",     value: carrierStats.total_bids,       color: "#38BDF8" },
                    { label: "Transfers",       value: carrierStats.total_transfers,  color: GOLD },
                    { label: "Avg Fee",         value: `$${(carrierStats.avg_placement_fee || 0).toLocaleString()}`, color: "#4ADE80" },
                  ].map((s) => (
                    <div key={s.label} className="p-3 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                      <p className="text-[9px] text-white/40 uppercase tracking-[0.15em] font-head">{s.label}</p>
                      <p className="font-head font-black text-lg mt-0.5" style={{ color: s.color }}>{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Leaderboard table */}
                {carrierStats.leaderboard?.length === 0 ? (
                  <p className="text-xs text-white/25 text-center py-3">No carrier bids placed yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                          {["#", "Carrier", "Bids", "Wins", "Win %", "Avg Fee"].map((h) => (
                            <th key={h} className="text-left pb-2 pr-3 font-head text-[9px] uppercase tracking-[0.15em] text-white/35">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/05">
                        {carrierStats.leaderboard.map((row, i) => (
                          <tr key={row.carrier_name} data-testid={`carrier-stats-row-${i}`}
                            style={{ background: i === 0 ? "rgba(245,200,66,0.04)" : "transparent" }}>
                            <td className="py-2.5 pr-3">
                              <span className="font-head text-[10px] font-bold" style={{ color: i === 0 ? GOLD : i === 1 ? "#9CA3AF" : i === 2 ? "#CD7F32" : "rgba(255,255,255,0.25)" }}>
                                {i + 1}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4 font-head font-bold text-white max-w-[120px] truncate">{row.carrier_name}</td>
                            <td className="py-2.5 pr-3 text-white/50">{row.bids_placed}</td>
                            <td className="py-2.5 pr-3" style={{ color: row.wins > 0 ? "#4ADE80" : "rgba(255,255,255,0.3)" }}>{row.wins}</td>
                            <td className="py-2.5 pr-3">
                              <span className="font-head text-[10px]" style={{ color: row.win_rate >= 50 ? "#4ADE80" : row.win_rate >= 20 ? GOLD : "rgba(255,255,255,0.4)" }}>
                                {row.win_rate}%
                              </span>
                            </td>
                            <td className="py-2.5 font-head font-bold" style={{ color: GOLD }}>
                              {row.avg_placement_fee ? `$${row.avg_placement_fee.toLocaleString()}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button onClick={fetchCarrierStats} disabled={loadingStats}
                  data-testid="admin-refresh-carrier-stats"
                  className="flex items-center gap-1.5 text-[9px] font-head uppercase tracking-[0.15em] text-white/30 hover:text-[#38BDF8] transition-colors disabled:opacity-40">
                  <TrendingUp className="w-3 h-3" /> Refresh Stats
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── TRANSFER BOARD ── */}
      <div style={{ background: MID, border: `1px solid rgba(245,200,66,0.2)` }}>
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <Trophy className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Transfer Board</p>
            <p className="text-[10px] text-white/30 mt-0.5">Bids go live instantly. Pick the winning carrier for each driver.</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
            {Object.keys(bidsByDriver).length} driver{Object.keys(bidsByDriver).length !== 1 ? "s" : ""}
          </span>
        </div>
        {Object.keys(bidsByDriver).length > 0 && (
          <div className="px-4 pt-3 pb-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "rgba(255,255,255,0.2)" }} />
              <input type="text" data-testid="admin-tb-search" placeholder="Search by driver name or state…"
                value={tbSearch} onChange={(e) => { setTbSearch(e.target.value); setTbPage(0); }}
                className="w-full bg-[#111] text-white text-sm pl-9 pr-4 py-2 focus:outline-none"
                style={{ border: "1px solid rgba(255,255,255,0.08)" }} />
              {tbSearch && <button onClick={() => { setTbSearch(""); setTbPage(0); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"><X className="w-3.5 h-3.5" /></button>}
            </div>
          </div>
        )}
        {Object.keys(bidsByDriver).length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-white/25 text-sm">No live bids yet. Carriers will bid when drivers appear on the board.</p>
          </div>
        ) : tbDriverEntries.length === 0 ? (
          <div className="px-4 py-6 text-center"><p className="text-white/25 text-sm">No drivers match "{tbSearch}"</p></div>
        ) : (
          <>
            <div className="divide-y divide-white/05">
              {tbPaged.map(([driverId, driverBids]) => {
                const driver = drivers.find((d) => d.id === driverId);
                const driverName = driverBids[0]?.driver_name || driverId;
                const alreadyTransferred = driver?.market_status === "transferred";

                // Days on Market badge
                const dateVal = driver?.market_listed_at || driver?.created_at;
                const daysOnMarket = dateVal
                  ? Math.min(12, Math.max(1, Math.floor((Date.now() - new Date(dateVal).getTime()) / 86400000)))
                  : null;
                const domColor  = daysOnMarket == null ? null
                  : daysOnMarket <= 5  ? "#4ade80"
                  : daysOnMarket <= 8  ? "#f5c842"
                  : "#FF4D00";
                const domBg     = daysOnMarket == null ? null
                  : daysOnMarket <= 5  ? "rgba(74,222,128,0.08)"
                  : daysOnMarket <= 8  ? "rgba(245,200,66,0.10)"
                  : "rgba(255,77,0,0.12)";
                const domBorder = daysOnMarket == null ? null
                  : daysOnMarket <= 5  ? "rgba(74,222,128,0.25)"
                  : daysOnMarket <= 8  ? "rgba(245,200,66,0.30)"
                  : "rgba(255,77,0,0.35)";

                return (
                  <div key={driverId} className="px-4 py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Truck className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                      <p className="font-head text-sm font-bold uppercase tracking-[0.08em] text-white">{driverName}</p>
                      {driver?.state && (
                        <span className="font-head text-[9px] uppercase px-1.5 py-0.5"
                          style={{ background: "rgba(255,77,0,0.1)", color: ACCENT, border: "1px solid rgba(255,77,0,0.25)" }}>{driver.state}</span>
                      )}
                      {daysOnMarket != null && !alreadyTransferred && (
                        <span
                          data-testid={`days-on-market-${driverId}`}
                          className="font-head text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 flex items-center gap-1"
                          style={{ background: domBg, color: domColor, border: `1px solid ${domBorder}` }}
                          title="Days listed on market"
                        >
                          <Clock className="w-2.5 h-2.5" />{daysOnMarket}d
                        </span>
                      )}
                      {alreadyTransferred ? (
                        <div className="ml-auto flex items-center gap-2 flex-wrap">
                          <span className="font-head text-[9px] uppercase px-2 py-0.5"
                            style={{ background: "rgba(245,200,66,0.2)", color: GOLD, border: "1px solid rgba(245,200,66,0.4)" }}>
                            TRANSFERRED → {driver?.transferred_to}
                          </span>
                          <button
                            data-testid={`return-to-market-btn-${driverId}`}
                            onClick={() => handleReturnToMarket(driverId, driverName)}
                            disabled={returningToMarket === driverId}
                            className="flex items-center gap-1.5 px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:brightness-110 disabled:opacity-50"
                            style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)", color: "#4ADE80" }}
                          >
                            {returningToMarket === driverId ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                            Return to Market
                          </button>
                        </div>
                      ) : (
                        <span className="ml-auto text-[10px] text-white/30">{driverBids.length} bid{driverBids.length !== 1 ? "s" : ""}</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {driverBids.map((b) => {
                        const isExpanded = expandedBidIds.has(b.id);
                        const isDeclining = decliningBidId === b.id;
                        const offerLines = (b.bid_message || "").trim().split("\n").filter(Boolean);
                        return (
                        <div key={b.id}
                          style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${isExpanded ? "rgba(245,200,66,0.18)" : "rgba(255,255,255,0.06)"}` }}>
                          {/* ── Clickable header row ── */}
                          <div
                            className="flex flex-wrap items-center gap-3 px-4 py-3 cursor-pointer select-none"
                            onClick={() => toggleBidExpanded(b.id)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2.5 mb-0.5">
                                {(() => {
                                  const cKey = (b.carrier_name || "").toLowerCase();
                                  const profile = carrierProfileMap[cKey];
                                  const displayName = profile?.carrier_name_display || b.carrier_name;
                                  return (
                                    <>
                                      <CarrierLogoThumb carrierKey={cKey} name={displayName} />
                                      <p
                                        data-testid={`bid-carrier-name-${b.id}`}
                                        className="font-head text-sm font-bold text-white truncate"
                                        title={displayName !== b.carrier_name ? `Login name: ${b.carrier_name}` : undefined}
                                      >
                                        {displayName}
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                              <p className="text-xs text-white/50 mt-1 leading-relaxed">
                                {b.position_type?.replace("_", " ")} · {b.equipment_package} ·{" "}
                                {b.pay_program === "company_driver"
                                  ? `${b.driver_pay_cpm}¢ CPM`
                                  : `${b.driver_pay_percentage}% ${(b.pay_program || "").replace("_", " ")}`}{" "}
                                · <span style={{ color: GOLD }}>${b.placement_fee?.toLocaleString()}</span>
                                {" · "}{b.payment_option === "weekly_flex" ? `Flex $${b.weekly_flex_price}/wk` : b.payment_option === "one_time_discount" ? "One-Time" : "50/50 Split"}
                                {offerLines.length > 0 && <span style={{ color: "rgba(245,200,66,0.5)" }}> · {offerLines.length} offer detail{offerLines.length !== 1 ? "s" : ""}</span>}
                              </p>
                            </div>
                            {/* Expand chevron */}
                            <ChevronDown
                              className="w-3.5 h-3.5 shrink-0 transition-transform duration-200"
                              style={{ color: "rgba(255,255,255,0.25)", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                            />
                            {!alreadyTransferred && (
                              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end"
                                onClick={(e) => e.stopPropagation()}>
                                {/* Decline */}
                                {isDeclining ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] text-white/40 font-head uppercase tracking-[0.1em]">Sure?</span>
                                    <button
                                      data-testid={`decline-confirm-btn-${b.id}`}
                                      onClick={() => { onBidStatusChange(b.id, "declined"); setDecliningBidId(null); }}
                                      className="px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em]"
                                      style={{ background: "rgba(239,68,68,0.18)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
                                      Yes →
                                    </button>
                                    <button onClick={() => setDecliningBidId(null)}
                                      className="text-white/30 hover:text-white/70 p-1">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    data-testid={`decline-btn-${b.id}`}
                                    onClick={() => { setDecliningBidId(b.id); setCounterOfferBidId(null); }}
                                    className="px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all hover:brightness-110"
                                    style={{ background: "rgba(239,68,68,0.05)", color: "rgba(239,68,68,0.65)", border: "1px solid rgba(239,68,68,0.18)" }}>
                                    Decline
                                  </button>
                                )}
                                {/* Counter-Offer */}
                                {counterOfferBidId === b.id ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] text-white/40 font-head uppercase tracking-[0.1em]">$</span>
                                    <input
                                      type="number"
                                      value={counterOfferFee}
                                      onChange={(e) => setCounterOfferFee(e.target.value)}
                                      placeholder="Fee"
                                      className="w-20 px-2 py-1.5 text-xs font-head text-white bg-transparent outline-none"
                                      style={{ border: "1px solid rgba(245,200,66,0.45)" }}
                                      onKeyDown={(e) => e.key === "Enter" && handleSubmitCounterOffer(b.id)}
                                      autoFocus
                                    />
                                    <button onClick={() => handleSubmitCounterOffer(b.id)} disabled={counterOfferSending}
                                      className="px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] disabled:opacity-50"
                                      style={{ background: "rgba(245,200,66,0.15)", color: "#f5c842", border: "1px solid rgba(245,200,66,0.35)" }}>
                                      {counterOfferSending ? "…" : "Send →"}
                                    </button>
                                    <button onClick={() => { setCounterOfferBidId(null); setCounterOfferFee(""); }}
                                      className="text-white/30 hover:text-white/70 p-1">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    data-testid={`counter-offer-btn-${b.id}`}
                                    onClick={() => { setCounterOfferBidId(b.id); setCounterOfferFee(""); setDecliningBidId(null); }}
                                    className="px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all hover:brightness-110"
                                    style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                    Counter →
                                  </button>
                                )}
                                <button onClick={() => onTransferDriver(driverId, b.id, b.carrier_name)}
                                  data-testid={`transfer-btn-${b.id}`}
                                  className="px-4 py-2 font-head text-xs font-black uppercase tracking-[0.2em] transition-all hover:brightness-110"
                                  style={{ background: GOLD, color: DARK }}>
                                  Transfer Driver →
                                </button>
                              </div>
                            )}
                          </div>
                          {/* ── Expandable full offer details ── */}
                          {isExpanded && (
                            <div className="px-4 pb-4 pt-0">
                              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} className="pt-3">
                                {offerLines.length > 0 ? (
                                  <div className="grid grid-cols-1 gap-1.5">
                                    {offerLines.map((line, i) => {
                                      const colonIdx = line.indexOf(":");
                                      const label = colonIdx > -1 ? line.slice(0, colonIdx).trim() : line;
                                      const value = colonIdx > -1 ? line.slice(colonIdx + 1).trim() : "";
                                      return (
                                        <div key={i} className="flex items-baseline gap-3 text-xs">
                                          <span className="flex items-center gap-1 font-head text-[9px] uppercase tracking-[0.18em] shrink-0 w-28 truncate" style={{ color: "rgba(255,255,255,0.28)" }}>
                                            {(() => {
                                              const k = label.toLowerCase();
                                              const Icon =
                                                k === "truck"               ? Truck :
                                                k === "home time"           ? Clock :
                                                k === "miles/week"          ? MapPin :
                                                k === "signup bonus"        ? DollarSign :
                                                k === "safety bonus"        ? Shield :
                                                k === "driver of the week"  ? Trophy :
                                                k === "driver of the month" ? Star :
                                                FileText;
                                              return <Icon className="w-3 h-3 shrink-0 opacity-60" />;
                                            })()}
                                            {label}
                                          </span>
                                          <span className="text-white/70">{value || line}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-xs italic" style={{ color: "rgba(255,255,255,0.25)" }}>
                                    No additional offer details — carrier submitted placement fee only.
                                  </p>
                                )}
                                <p className="text-[10px] mt-3" style={{ color: "rgba(255,255,255,0.2)" }}>
                                  Submitted {timeAgo(b.created_at)}{b.cashback_applied > 0 ? ` · $${b.cashback_applied} cashback applied` : ""}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {tbTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="text-[10px] text-white/30 font-head uppercase tracking-[0.1em]">
                  {tbDriverEntries.length} driver{tbDriverEntries.length !== 1 ? "s" : ""} · page {tbPage + 1} of {tbTotalPages}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => setTbPage((p) => Math.max(0, p - 1))} disabled={tbPage === 0}
                    className="w-7 h-7 flex items-center justify-center disabled:opacity-30 text-white/40 hover:text-white"
                    style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {Array.from({ length: tbTotalPages }).map((_, i) => (
                    <button key={i} onClick={() => setTbPage(i)}
                      className="w-7 h-7 flex items-center justify-center font-head text-[10px] transition-colors"
                      style={{ background: i === tbPage ? GOLD : "transparent", color: i === tbPage ? DARK : "rgba(255,255,255,0.3)", border: `1px solid ${i === tbPage ? GOLD : "rgba(255,255,255,0.1)"}` }}>
                      {i + 1}
                    </button>
                  ))}
                  <button onClick={() => setTbPage((p) => Math.min(tbTotalPages - 1, p + 1))} disabled={tbPage === tbTotalPages - 1}
                    className="w-7 h-7 flex items-center justify-center disabled:opacity-30 text-white/40 hover:text-white"
                    style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Transferred Drivers — Return to Market ── */}
      {(() => {
        const transferredDrivers = drivers.filter((d) => d.market_status === "transferred");
        if (transferredDrivers.length === 0) return null;
        return (
          <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }} data-testid="transferred-drivers-panel">
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <TrendingUp className="w-4 h-4 shrink-0" style={{ color: "#4ADE80" }} />
              <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Transferred Drivers</p>
              <span className="ml-auto font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]" style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>{transferredDrivers.length} transferred</span>
            </div>
            <div className="divide-y divide-white/05">
              {transferredDrivers.map((d) => {
                // Find the winning bid for this driver to get bid_id for annulment
                const linkedBid = bids.find(b =>
                  b.driver_id === d.id &&
                  ["payment_pending", "receipt_uploaded", "transferred"].includes(b.status)
                );
                const isAnnulling = annullingId === d.id;
                return (
                  <div key={d.id}>
                    <div className="px-4 py-3 flex items-center flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-head text-sm font-bold text-white">{d.first_name} {d.last_name}</p>
                          {d.contact_opted_out && (
                            <span data-testid={`admin-optout-badge-board-${d.id}`}
                              className="font-head text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5 shrink-0"
                              style={{ background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}>
                              OPT-OUT
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-white/35 mt-0.5">{d.state} · {d.cdl_class} · {d.freight_types || "—"}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: GOLD }}>→ {d.transferred_to || "Unknown Carrier"}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end"
                        onClick={(e) => e.stopPropagation()}>
                        {/* Share link */}
                        {d.public_slug && (
                          <button
                            data-testid={`admin-share-board-${d.id}`}
                            onClick={() => {
                              const url = `https://hylascompanion.com/driver/${d.public_slug}`;
                              navigator.clipboard.writeText(url).then(() => toast.success("Share link copied"));
                            }}
                            title="Copy share link"
                            className="flex items-center gap-1 px-2 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors"
                            style={{ borderColor: "rgba(255,77,0,0.2)", color: "rgba(255,77,0,0.6)", background: "transparent" }}>
                            <Share2 className="w-3 h-3" />
                          </button>
                        )}
                        {/* Annul Agreement */}
                        {linkedBid && (
                          isAnnulling ? (
                            <button onClick={() => { setAnnullingId(null); setAnnulReason(""); setAnnulNotes(""); }}
                              className="flex items-center gap-1 px-2 py-1.5 font-head text-[9px] uppercase tracking-[0.12em]"
                              style={{ color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.1)" }}>
                              <X className="w-3 h-3" /> Cancel
                            </button>
                          ) : (
                            <button
                              data-testid={`annul-btn-${d.id}`}
                              onClick={() => { setAnnullingId(d.id); setAnnulBidId(linkedBid.id); setAnnulReason(""); setAnnulNotes(""); }}
                              className="flex items-center gap-1.5 px-3 py-2 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:brightness-110"
                              style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "rgba(239,68,68,0.7)" }}>
                              Annul Agreement
                            </button>
                          )
                        )}
                        <button
                          data-testid={`return-to-market-btn-${d.id}`}
                          onClick={() => handleReturnToMarket(d.id, `${d.first_name} ${d.last_name}`)}
                          disabled={returningToMarket === d.id}
                          className="flex items-center gap-1.5 px-3 py-2 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:brightness-110 disabled:opacity-50"
                          style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)", color: "#4ADE80" }}
                        >
                          {returningToMarket === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                          Return to Market
                        </button>
                      </div>
                    </div>
                    {/* Annul inline form */}
                    {isAnnulling && (
                      <div className="px-4 pb-4"
                        style={{ borderTop: "1px solid rgba(239,68,68,0.12)", background: "rgba(239,68,68,0.03)" }}>
                        <p className="font-head text-[9px] uppercase tracking-[0.18em] mb-2 pt-3" style={{ color: "rgba(239,68,68,0.6)" }}>
                          Annul Agreement — {d.first_name} {d.last_name}
                        </p>
                        <div className="flex flex-col gap-2">
                          <select
                            data-testid={`annul-reason-${d.id}`}
                            value={annulReason}
                            onChange={e => setAnnulReason(e.target.value)}
                            className="w-full px-3 py-2 text-xs font-head text-white bg-transparent outline-none"
                            style={{ border: "1px solid rgba(239,68,68,0.3)", background: "#0d0d0d", color: annulReason ? "#fff" : "rgba(255,255,255,0.3)" }}>
                            <option value="" disabled style={{ color: "#999" }}>— Select reason (required) —</option>
                            <option value="Non-Payment">Non-Payment</option>
                            <option value="Payment Arrears">Payment Arrears</option>
                            <option value="Fraud / Misrepresentation">Fraud / Misrepresentation</option>
                            <option value="Contract Breach">Contract Breach</option>
                            <option value="Driver Withdrew">Driver Withdrew</option>
                            <option value="Other">Other</option>
                          </select>
                          <textarea
                            data-testid={`annul-notes-${d.id}`}
                            value={annulNotes}
                            onChange={e => setAnnulNotes(e.target.value)}
                            placeholder="Optional notes for carrier (max 500 chars)…"
                            maxLength={500}
                            rows={2}
                            className="w-full px-3 py-2 text-xs text-white bg-transparent outline-none resize-none placeholder-white/20"
                            style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                          />
                          <button
                            data-testid={`annul-confirm-btn-${d.id}`}
                            onClick={handleAnnulConfirm}
                            disabled={!annulReason || annulSending}
                            className="px-4 py-2 font-head text-xs font-black uppercase tracking-[0.18em] transition-all hover:brightness-110 disabled:opacity-40"
                            style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
                            {annulSending ? "Confirming…" : "Confirm Annulment →"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Annulment Records Table ── */}
      {(() => {
        const SORT_COLS = [
          { key: "created_at",       label: "Date" },
          { key: "carrier_name",     label: "Carrier" },
          { key: "driver_name",      label: "Driver" },
          { key: "annulment_reason", label: "Reason" },
        ];
        const rows = annulmentsData?.annulments || [];
        const total = annulmentsData?.total || 0;
        const totalPages = Math.max(1, Math.ceil(total / 50));
        const SortIcon = ({ col }) => annulTableSort.by === col
          ? <span className="ml-1" style={{ color: ACCENT }}>{annulTableSort.dir === -1 ? "▼" : "▲"}</span>
          : <span className="ml-1 text-white/20">↕</span>;

        return (
          <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }} data-testid="annulments-table-section">
            {/* Header */}
            <div className="px-4 py-3 flex items-center flex-wrap gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <FileText className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
              <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Annulment Records</p>
              {total > 0 && (
                <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                  style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                  {total} total
                </span>
              )}
              {/* CSV Export */}
              <button
                data-testid="export-annulments-csv"
                onClick={handleExportCSV}
                disabled={csvExporting || rows.length === 0}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] border transition-all disabled:opacity-40"
                style={{ borderColor: "rgba(74,222,128,0.3)", color: "#4ADE80", background: "transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.06)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                {csvExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Export CSV
              </button>
            </div>

            {annulTableLoading ? (
              <div className="px-4 py-6 flex items-center gap-2 text-white/30">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-head text-xs uppercase tracking-[0.2em]">Loading…</span>
              </div>
            ) : rows.length === 0 ? (
              <p className="px-4 py-5 text-xs text-white/25 font-head uppercase tracking-[0.2em]">No annulments on record.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="annulments-table">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
                      {SORT_COLS.map(col => (
                        <th key={col.key}
                          onClick={() => handleAnnulSort(col.key)}
                          className="px-4 py-2.5 font-head text-[9px] uppercase tracking-[0.2em] text-left cursor-pointer select-none hover:text-white transition-colors"
                          style={{ color: annulTableSort.by === col.key ? "#e8e8e8" : "rgba(255,255,255,0.3)" }}>
                          {col.label}<SortIcon col={col.key} />
                        </th>
                      ))}
                      <th className="px-4 py-2.5 font-head text-[9px] uppercase tracking-[0.2em] text-left text-white/25">Notes</th>
                      <th className="px-4 py-2.5 font-head text-[9px] uppercase tracking-[0.2em] text-left text-white/25">Ack</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      let dateStr = "—";
                      try { dateStr = new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch {}
                      return (
                        <tr key={r.notification_id || i}
                          data-testid={`annulment-row-${i}`}
                          style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                          <td className="px-4 py-2.5 text-white/40 whitespace-nowrap">{dateStr}</td>
                          <td className="px-4 py-2.5 font-head font-bold text-white">{r.carrier_name || "—"}</td>
                          <td className="px-4 py-2.5 text-white/70">{r.driver_name || "—"}</td>
                          <td className="px-4 py-2.5">
                            <span className="font-head text-[9px] font-black uppercase px-2 py-0.5 whitespace-nowrap"
                              style={{ background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                              {r.annulment_reason || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-white/30 max-w-[160px] truncate" title={r.annulment_notes || ""}>
                            {r.annulment_notes || "—"}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-head text-[8px] font-black uppercase px-1.5 py-0.5"
                              style={{ background: r.acknowledged ? "rgba(74,222,128,0.1)" : "rgba(255,255,255,0.05)", color: r.acknowledged ? "#4ADE80" : "#555" }}>
                              {r.acknowledged ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 flex items-center gap-3 border-t border-white/5">
                    <button disabled={annulTablePage <= 1} onClick={() => setAnnulTablePage(p => p - 1)}
                      className="px-3 py-1 font-head text-[9px] uppercase tracking-[0.15em] border border-white/10 text-white/40 hover:text-white disabled:opacity-30">← Prev</button>
                    <span className="font-head text-[9px] uppercase tracking-[0.15em] text-white/30">
                      Page {annulTablePage} / {totalPages} · {total} total
                    </span>
                    <button disabled={annulTablePage >= totalPages} onClick={() => setAnnulTablePage(p => p + 1)}
                      className="px-3 py-1 font-head text-[9px] uppercase tracking-[0.15em] border border-white/10 text-white/40 hover:text-white disabled:opacity-30">Next →</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Referral Payouts ── */}
      {pendingReferrals.length > 0 && (() => {
        const unpaid = pendingReferrals.filter(r => r.market_status === "transferred" && !r.referral_bonus_paid);
        const paid   = pendingReferrals.filter(r => r.referral_bonus_paid);
        if (unpaid.length === 0 && paid.length === 0) return null;
        return (
          <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }} data-testid="referral-payouts-panel">
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <Users className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
              <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Referral Payouts</p>
              {unpaid.length > 0 && (
                <span className="ml-auto font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                  style={{ background: "rgba(245,200,66,0.12)", color: GOLD, border: "1px solid rgba(245,200,66,0.3)" }}>
                  {unpaid.length} pending · ${unpaid.length * 400}
                </span>
              )}
            </div>
            <div className="divide-y divide-white/5">
              {[...unpaid, ...paid].map(r => (
                <div key={r.id} className="px-4 py-3 flex items-center flex-wrap gap-3" data-testid={`referral-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-sm font-bold text-white">{r.first_name} {r.last_name}</p>
                    <p className="text-[10px] text-white/35 mt-0.5">
                      {r.state} · {r.cdl_class} · referred by <span className="text-white/60">{r.referrer_name}</span>
                      {r.transferred_to && <span> → {r.transferred_to}</span>}
                    </p>
                  </div>
                  {r.referral_bonus_paid ? (
                    <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em]"
                      style={{ background: "rgba(74,222,128,0.08)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.25)" }}>
                      $400 Paid
                    </span>
                  ) : r.market_status === "transferred" ? (
                    <button
                      data-testid={`mark-referral-paid-${r.id}`}
                      disabled={markingReferralPaid === r.id}
                      onClick={() => handleMarkReferralPaid(r.id, `${r.first_name} ${r.last_name}`, r.referrer_name)}
                      className="flex items-center gap-1.5 px-3 py-2 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:brightness-110 disabled:opacity-50"
                      style={{ background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)", color: GOLD }}
                    >
                      {markingReferralPaid === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                      Mark $400 Paid
                    </button>
                  ) : (
                    <span className="font-head text-[9px] px-2 py-0.5 text-white/30 uppercase tracking-[0.1em]">{r.market_status}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Driver face crop */}
      <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Driver Face Photos</p>
        </div>
        <div className="divide-y divide-white/05 max-h-64 overflow-y-auto">
          {drivers.filter((d) => d.has_cdl && !d.market_photo_path).map((d) => (
            <div key={d.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-white">{d.first_name} {d.last_name}</p>
                <p className="text-[10px] text-white/30">Has CDL · No face crop yet</p>
              </div>
              <button onClick={() => onCropFace(d.id)}
                className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] transition-colors hover:brightness-110"
                style={{ background: ACCENT, color: "white" }}>Crop Face</button>
            </div>
          ))}
          {drivers.filter((d) => d.has_cdl && !d.market_photo_path).length === 0 && (
            <p className="px-4 py-4 text-xs text-white/30">All drivers with CDLs have face photos.</p>
          )}
        </div>
      </div>

      {/* ── Driver Bonus Approvals ── */}
      <div style={{ background: MID, border: `1px solid rgba(74,222,128,0.2)` }}>
        <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <DollarSign className="w-4 h-4" style={{ color: "#4ADE80" }} />
          <div>
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Driver Bonus Approvals</p>
            <p className="text-[10px] text-white/30 mt-0.5">Approve earned bonuses — $65 docs · $15 drug screen · $20 start date</p>
          </div>
          <span className="ml-auto font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.2)" }}>
            {bonusRequests.filter(r =>
              r.bonus_docs_status === "pending" || r.bonus_drug_status === "pending" || r.bonus_start_status === "pending" ||
              r.bonus_carrier_start_status === "pending_approval" || r.bonus_carrier_week2_status === "pending_approval" ||
              r.bonus_carrier_onetime_status === "pending_approval" || r.bonus_signup_status === "pending_approval"
            ).length} pending
          </span>
        </div>
        {bonusRequests.length === 0 ? (
          <p className="px-4 py-6 text-xs text-white/25 text-center">No drivers with bonus activity yet.</p>
        ) : (
          <div className="divide-y divide-white/05">
            {bonusRequests.map((req) => {
              const planType = req.bonus_carrier_plan_type;
              const weeklyPrice = req.bonus_carrier_weekly_price || 0;
              const weeklyRoyalty = +(weeklyPrice * 0.10).toFixed(2);
              const baseItems = [
                { type: "docs",        label: "$65 — Docs",        status: req.bonus_docs_status,  date: null },
                { type: "drug_screen", label: "$15 — Drug Screen", status: req.bonus_drug_status,  date: req.drug_screen_date },
                { type: "start",       label: "$20 — Start Date",  status: req.bonus_start_status, date: req.start_date },
              ];
              const carrierItems = [];
              if (planType === "weekly_flex") {
                carrierItems.push({ type: "carrier_start", label: `$${weeklyRoyalty}/wk Weekly`, status: req.bonus_carrier_start_status });
              } else if (planType === "standard") {
                carrierItems.push({ type: "carrier_start",  label: "$50 — Hire Start", status: req.bonus_carrier_start_status });
                carrierItems.push({ type: "carrier_week2",  label: "$50 — 2 Weeks",   status: req.bonus_carrier_week2_status });
              } else if (planType === "one_time_discount") {
                carrierItems.push({ type: "carrier_onetime", label: "$75 — One-Time", status: req.bonus_carrier_onetime_status });
              }
              if (planType) {
                carrierItems.push({ type: "carrier_signup", label: "??? Signup Bonus", status: req.bonus_signup_status, isMystery: true });
              }
              const renderBonusItem = ({ type, label, status, date, isMystery }) => {
                const isPending  = status === "pending" || status === "pending_approval";
                const isApproved = status === "approved";
                const key = `${req.id}-${type}`;
                return (
                  <div key={type} className="p-2.5 flex flex-col gap-1.5"
                    style={{
                      background: isApproved ? "rgba(74,222,128,0.07)" : isPending ? "rgba(255,77,0,0.06)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${isApproved ? "rgba(74,222,128,0.25)" : isPending ? "rgba(255,77,0,0.2)" : "rgba(255,255,255,0.05)"}`,
                    }}>
                    <p className="font-head text-[9px] font-bold uppercase tracking-[0.1em] text-white/70">{label}{isMystery && " ✦"}</p>
                    {date && <p className="text-[9px] text-white/30">{date}</p>}
                    {isApproved ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-head text-[8px] uppercase px-1.5 py-0.5 w-fit"
                          style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.25)" }}>✓ Approved</span>
                        <button
                          disabled={approvingBonus === `${req.id}-${type}-revoke`}
                          onClick={() => unapproveBonus(req.id, type)}
                          data-testid={`admin-revoke-bonus-${req.id}-${type}`}
                          className="font-head text-[8px] uppercase px-1.5 py-0.5 w-fit transition-all hover:brightness-110 disabled:opacity-40"
                          style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                          {approvingBonus === `${req.id}-${type}-revoke` ? "…" : "Revoke"}
                        </button>
                      </div>
                    ) : isPending ? (
                      <div className="flex flex-col gap-1">
                        {isMystery && type === "carrier_signup" && (
                          <input type="number" min="0" placeholder="$ amount"
                            data-testid={`admin-signup-amount-${req.id}`}
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white text-[9px] px-1.5 py-1 focus:outline-none"
                            onChange={(e) => { document.getElementById(`signup-amt-${req.id}`)?.setAttribute("data-amount", e.target.value); }}
                            id={`signup-amt-${req.id}`} />
                        )}
                        <button disabled={approvingBonus === key}
                          onClick={() => {
                            const amtEl = document.getElementById(`signup-amt-${req.id}`);
                            const amt = amtEl ? amtEl.getAttribute("data-amount") : null;
                            approveBonus(req.id, type, amt ? { signup_amount: parseFloat(amt) } : {});
                          }}
                          data-testid={`admin-approve-bonus-${req.id}-${type}`}
                          className="font-head text-[8px] uppercase px-2 py-1 transition-all hover:brightness-110 disabled:opacity-40 w-fit"
                          style={{ background: "#4ADE80", color: DARK }}>
                          {approvingBonus === key ? "…" : "Approve"}
                        </button>
                      </div>
                    ) : (
                      <span className="font-head text-[8px] uppercase text-white/15">Incomplete</span>
                    )}
                  </div>
                );
              };
              return (
                <div key={req.id} className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="w-3.5 h-3.5 shrink-0 text-white/40" />
                    <p className="font-head text-sm font-bold text-white">{req.name}</p>
                    {req.state && (
                      <span className="font-head text-[9px] px-1.5 py-0.5 uppercase"
                        style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>{req.state}</span>
                    )}
                    {planType && (
                      <span className="font-head text-[8px] px-1.5 py-0.5 uppercase ml-auto"
                        style={{ background: "rgba(245,200,66,0.08)", color: GOLD, border: "1px solid rgba(245,200,66,0.2)" }}>
                        {planType === "weekly_flex" ? "Weekly Plan" : planType === "standard" ? "50/50 Plan" : "One-Time"}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">{baseItems.map(renderBonusItem)}</div>
                  {carrierItems.length > 0 && (
                    <>
                      <p className="font-head text-[8px] uppercase tracking-[0.2em] text-white/20 mt-3 mb-1.5">After Carrier Hire</p>
                      <div className="grid grid-cols-2 gap-2">{carrierItems.map(renderBonusItem)}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Driver Document Manager ── */}
      <div style={{ background: MID, border: "1px solid rgba(74,222,128,0.15)" }}>
        <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <FileText className="w-4 h-4" style={{ color: "#4ADE80" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Driver Document Manager</p>
            <p className="text-[10px] text-white/30 mt-0.5">Upload, view or remove CDL, Medical, MVR, PSP and Clearinghouse docs per driver</p>
          </div>
          <button onClick={loadAdminDocs}
            className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border border-white/10 text-white/40 hover:border-[#4ADE80] hover:text-[#4ADE80] transition-colors">
            {adminDocLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
          </button>
        </div>
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
            <input type="text" value={adminDocSearch} onChange={(e) => setAdminDocSearch(e.target.value)}
              placeholder="Search driver name…" data-testid="admin-doc-search"
              className="w-full bg-[#0A0A0A] border border-white/08 text-white pl-8 pr-3 py-2 text-xs focus:outline-none focus:border-[#4ADE80] placeholder:text-white/20" />
          </div>
        </div>
        {adminDocLoading ? (
          <div className="px-4 py-6 text-center text-white/30 text-sm">Loading…</div>
        ) : adminDocs.length === 0 ? (
          <div className="px-4 py-6 text-center text-white/25 text-xs">No market drivers found. Load real drivers first.</div>
        ) : (
          <div className="divide-y divide-white/05 max-h-[600px] overflow-y-auto">
            {adminDocs.filter((d) => d.name.toLowerCase().includes(adminDocSearch.toLowerCase())).map((driver) => {
              const isExpanded = adminDocExpanded.has(driver.id);
              const uploadedCount = ADMIN_DOC_TYPES.filter((t) => !!driver[t.pathKey]).length;
              return (
                <div key={driver.id}>
                  <button data-testid={`admin-doc-driver-${driver.id}`}
                    onClick={() => setAdminDocExpanded((prev) => {
                      const next = new Set(prev);
                      isExpanded ? next.delete(driver.id) : next.add(driver.id);
                      return next;
                    })}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/02 transition-colors">
                    <Truck className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-head text-sm font-bold text-white truncate">{driver.name}</p>
                      {driver.state && <p className="text-[10px] text-white/30">{driver.state}</p>}
                    </div>
                    <span className="font-head text-[9px] uppercase px-2 py-0.5 shrink-0"
                      style={{
                        background: uploadedCount === ADMIN_DOC_TYPES.length ? "rgba(74,222,128,0.1)" : uploadedCount > 0 ? "rgba(255,77,0,0.08)" : "rgba(255,255,255,0.04)",
                        color: uploadedCount === ADMIN_DOC_TYPES.length ? "#4ADE80" : uploadedCount > 0 ? ACCENT : "#555",
                        border: `1px solid ${uploadedCount === ADMIN_DOC_TYPES.length ? "rgba(74,222,128,0.3)" : uploadedCount > 0 ? "rgba(255,77,0,0.25)" : "#2a2a2a"}`,
                      }}>{uploadedCount}/{ADMIN_DOC_TYPES.length} docs</span>
                    <ChevronDown className="w-3.5 h-3.5 text-white/25 shrink-0 transition-transform" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
                      {ADMIN_DOC_TYPES.map(({ key, label, pathKey }) => {
                        const path = driver[pathKey];
                        const hasDoc = !!path;
                        const uploadKey = `${driver.id}-${key}`;
                        const isUploading = !!adminDocUploading[uploadKey];
                        const isRemoving  = !!adminDocRemoving[uploadKey];
                        if (!adminDocFileRefs.current[uploadKey]) { adminDocFileRefs.current[uploadKey] = { current: null }; }
                        return (
                          <div key={key} className="p-3 flex flex-col gap-2"
                            style={{ background: hasDoc ? "rgba(74,222,128,0.05)" : "rgba(255,255,255,0.02)", border: `1px solid ${hasDoc ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.06)"}` }}>
                            <div className="flex items-center justify-between">
                              <p className="font-head text-[10px] font-bold uppercase tracking-[0.12em] text-white">{label}</p>
                              <span className="font-head text-[8px] uppercase px-1.5 py-0.5"
                                style={{ background: hasDoc ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)", color: hasDoc ? "#4ADE80" : "#555", border: `1px solid ${hasDoc ? "rgba(74,222,128,0.25)" : "#2a2a2a"}` }}>
                                {hasDoc ? "✓" : "○"}
                              </span>
                            </div>
                            <div className="flex flex-col gap-1.5 mt-auto">
                              {hasDoc && (
                                <button data-testid={`admin-doc-view-${driver.id}-${key}`} onClick={() => handleAdminDocView(path)}
                                  className="flex items-center gap-1 font-head text-[8px] uppercase tracking-[0.1em] text-white/50 hover:text-[#4ADE80] transition-colors">
                                  <ExternalLink className="w-3 h-3" /> View
                                </button>
                              )}
                              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic" className="hidden"
                                ref={(el) => { adminDocFileRefs.current[uploadKey].current = el; }}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAdminDocUpload(driver.id, key, f); e.target.value = ""; }} />
                              <button data-testid={`admin-doc-upload-${driver.id}-${key}`}
                                disabled={isUploading || isRemoving}
                                onClick={() => adminDocFileRefs.current[uploadKey].current?.click()}
                                className="flex items-center justify-center gap-1 py-1.5 font-head text-[8px] uppercase tracking-[0.1em] border transition-all disabled:opacity-50"
                                style={{ borderColor: hasDoc ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.12)", color: hasDoc ? "#4ADE80" : "rgba(255,255,255,0.6)", background: "transparent" }}>
                                {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                {hasDoc ? "Re-upload" : "Upload"}
                              </button>
                              {hasDoc && (
                                <button data-testid={`admin-doc-remove-${driver.id}-${key}`}
                                  disabled={isRemoving || isUploading} onClick={() => handleAdminDocRemove(driver.id, key)}
                                  className="flex items-center justify-center gap-1 py-1.5 font-head text-[8px] uppercase tracking-[0.1em] border border-red-500/20 text-red-400/60 hover:border-red-500/50 hover:text-red-400 transition-all disabled:opacity-40"
                                  style={{ background: "transparent" }}>
                                  {isRemoving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* ── DQ Bundle download + opt-out controls + share link ── */}
                      <div className="sm:col-span-5 mt-1 flex flex-col sm:flex-row gap-2">
                        <button
                          data-testid={`admin-dq-bundle-${driver.id}`}
                          onClick={() => handleAdminDQBundle(driver.id, driver.name)}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:border-[#F5C842]/50"
                          style={{ background: "rgba(245,200,66,0.06)", border: "1px solid rgba(245,200,66,0.2)", color: GOLD }}>
                          <Download className="w-3.5 h-3.5" /> Download DQ Bundle
                        </button>
                        {driver.public_slug && (
                          <button
                            data-testid={`admin-share-link-${driver.id}`}
                            onClick={() => {
                              const url = `https://hylascompanion.com/driver/${driver.public_slug}`;
                              navigator.clipboard.writeText(url).then(() => toast.success(`Share link copied — ${driver.name}`));
                            }}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:brightness-110"
                            style={{ background: "rgba(255,77,0,0.06)", border: "1px solid rgba(255,77,0,0.25)", color: ACCENT }}>
                            <Share2 className="w-3.5 h-3.5" /> Copy Share Link
                          </button>
                        )}

                        {driver.contact_opted_out ? (
                          <button
                            data-testid={`admin-revoke-optout-${driver.id}`}
                            onClick={() => handleAdminRevokeOptOut(driver.id)}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 font-head text-[9px] uppercase tracking-[0.15em] transition-all hover:border-green-500/50"
                            style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", color: "#4ADE80" }}>
                            <RotateCcw className="w-3.5 h-3.5" />
                            Re-enable Contact ({driver.opt_out_type === "sms_stop" ? "STOP" : "Call"})
                          </button>
                        ) : (
                          <>
                            <button
                              data-testid={`admin-log-sms-stop-${driver.id}`}
                              onClick={() => handleAdminOptOut(driver.id, "sms_stop")}
                              className="flex items-center justify-center gap-2 py-2.5 px-4 font-head text-[9px] uppercase tracking-[0.15em] transition-all"
                              style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171" }}>
                              <PhoneOff className="w-3.5 h-3.5" /> Log STOP
                            </button>
                            <button
                              data-testid={`admin-log-call-optout-${driver.id}`}
                              onClick={() => handleAdminOptOut(driver.id, "call_opt_out")}
                              className="flex items-center justify-center gap-2 py-2.5 px-4 font-head text-[9px] uppercase tracking-[0.15em] transition-all"
                              style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171" }}>
                              <PhoneOff className="w-3.5 h-3.5" /> Log Call Opt-Out
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Moderation Alerts ── */}
      <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="px-4 py-3 flex items-center gap-2 cursor-pointer" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }} onClick={() => setShowModPanel(!showModPanel)}>
          <Shield className="w-4 h-4 text-red-400" />
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Moderation Alerts</p>
          {modUnread > 0 && (
            <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(239,68,68,0.2)", color: "#f87171", border: "1px solid rgba(239,68,68,0.3)" }}>
              {modUnread} unread
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-white/30 transition-transform ${showModPanel ? "rotate-180" : ""} ${modUnread === 0 ? "ml-auto" : ""}`} />
        </div>
        {showModPanel && (
          <div className="divide-y divide-white/05">
            {modAlerts.length === 0 ? (
              <p className="px-4 py-3 text-xs text-white/30">No moderation activity yet.</p>
            ) : (
              modAlerts.map((a) => (
                <div key={a.id} className="px-4 py-3 flex flex-col gap-1" style={{ opacity: a.read ? 0.5 : 1 }}>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${a.is_ban ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                      {a.is_ban ? "PERMA BAN" : "WARNING"}
                    </span>
                    <span className="text-[9px] font-head uppercase" style={{ color: ACCENT }}>{a.sender_name}</span>
                    <span className="text-[9px] text-white/30">{a.sender_hub}</span>
                    <span className="text-[9px] text-white/20 ml-auto">{timeAgo(a.created_at)}</span>
                  </div>
                  <p className="text-xs text-white/60 italic">"{a.message}"</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-white/30">Reason: {a.reason.replace(/_/g, " ")}</span>
                    {!a.read && (
                      <button className="text-[9px] underline text-white/40 hover:text-white/70 ml-auto" onClick={() => markAlertRead(a.id)}>Mark read</button>
                    )}
                  </div>
                </div>
              ))
            )}
            {modBans.length > 0 && (
              <div className="px-4 py-3">
                <p className="font-head text-[9px] uppercase tracking-wider text-white/40 mb-2">Active Bans ({modBans.length})</p>
                <div className="space-y-1.5">
                  {modBans.map((b) => (
                    <div key={b.id} className="flex items-center gap-2 text-xs">
                      <span className="text-white/70">{b.sender_name}</span>
                      <span className="text-white/30">{b.sender_hub}</span>
                      <span className="text-white/20 ml-auto">{timeAgo(b.banned_at)}</span>
                      <button className="text-[9px] px-2 py-0.5 hover:opacity-80" style={{ background: "rgba(239,68,68,0.15)", color: "#f87171" }} onClick={() => unbanUser(b.id)}>Unban</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hub Credentials (change password) */}
      <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <Settings className="w-4 h-4" style={{ color: GOLD }} />
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Change Hub Credentials</p>
        </div>
        <div className="divide-y divide-white/05">
          {allHubs.map((hub) => (
            <div key={hub} className="px-4 py-3 flex flex-wrap items-center gap-3">
              <div className="flex-1">
                <p className="font-head text-xs font-bold uppercase tracking-[0.15em] text-white">{hub}</p>
              </div>
              {changingHub === hub ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input type="text" value={newUn} onChange={(e) => setNewUn(e.target.value)} placeholder="New username (optional)"
                    className="bg-[#0A0A0A] border border-white/10 text-white px-3 py-1.5 text-sm w-36 focus:outline-none focus:border-[#F5C842]" />
                  <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (optional)"
                    className="bg-[#0A0A0A] border border-white/10 text-white px-3 py-1.5 text-sm w-36 focus:outline-none focus:border-[#FF4D00]" />
                  <button onClick={() => changePassword(hub)} disabled={saving}
                    className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.1em] disabled:opacity-60"
                    style={{ background: GOLD, color: DARK }}>{saving ? "…" : "Save"}</button>
                  <button onClick={() => { setChangingHub(null); setNewPw(""); setNewUn(""); }} className="text-white/30 hover:text-white transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button onClick={() => { setChangingHub(hub); setNewPw(""); setNewUn(""); setPwMsg(""); }}
                  className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] border border-white/15 text-white/50 hover:border-[#FF4D00] hover:text-[#FF4D00] transition-colors">
                  Change
                </button>
              )}
            </div>
          ))}
        </div>
        {pwMsg && <p className="px-4 py-2 text-xs text-red-400">{pwMsg}</p>}
      </div>

      {/* ── Self-Apply Pending Review ── */}
      <div style={{ background: MID, border: `1px solid rgba(74,222,128,0.2)` }}>
        <button data-testid="admin-self-apply-toggle" onClick={() => setShowSelfApply((v) => !v)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showSelfApply ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <Users className="w-4 h-4 shrink-0" style={{ color: "#4ADE80" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Self-Apply Review</p>
            <p className="text-[10px] text-white/30 mt-0.5">Drivers who applied directly — awaiting your approval</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: selfApplyPending.length > 0 ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.05)", color: selfApplyPending.length > 0 ? "#4ADE80" : "rgba(255,255,255,0.3)", border: "1px solid rgba(74,222,128,0.25)" }}>
            {selfApplyPending.length} pending
          </span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showSelfApply ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showSelfApply && (
          <div className="divide-y divide-white/05 max-h-96 overflow-y-auto">
            {selfApplyPending.length === 0 ? (
              <p className="px-4 py-6 text-xs text-white/25 text-center">No pending applications.</p>
            ) : (
              selfApplyPending.map((p) => (
                <div key={p.id} className="px-4 py-3 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-sm font-bold text-white">{p.first_name} {p.last_name}</p>
                    <p className="text-[10px] text-white/40 mt-0.5">{p.state} · {p.cdl_class} · {p.position || "—"}</p>
                    <p className="font-mono text-[11px] mt-1" style={{ color: GOLD }}>{p.phone}</p>
                    {p.email && <p className="text-[10px] text-white/30">{p.email}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`self-apply-approve-${p.id}`} onClick={() => reviewSelfApply(p.id, "approve")} disabled={reviewingId === p.id}
                      className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.1em] disabled:opacity-60 transition-colors hover:brightness-110"
                      style={{ background: "#4ADE80", color: DARK }}>Approve</button>
                    <button data-testid={`self-apply-reject-${p.id}`} onClick={() => reviewSelfApply(p.id, "reject")} disabled={reviewingId === p.id}
                      className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.1em] border border-red-500/40 text-red-400 disabled:opacity-60 transition-colors hover:border-red-400">Reject</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ── Carrier Win History ── */}
      <div style={{ background: MID, border: `1px solid rgba(56,189,248,0.2)` }}>
        <button data-testid="admin-win-history-toggle" onClick={() => setShowWinHistory((v) => !v)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showWinHistory ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
          <DollarSign className="w-4 h-4 shrink-0" style={{ color: "#38BDF8" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Win Email History</p>
            <p className="text-[10px] text-white/30 mt-0.5">Log of all carrier win notifications sent by Resend</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(56,189,248,0.1)", color: "#38BDF8", border: "1px solid rgba(56,189,248,0.25)" }}>
            {winHistory.length} sent
          </span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showWinHistory ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showWinHistory && (
          <div>
            <div className="px-4 py-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <input data-testid="admin-win-history-search" type="text" value={winHistorySearch}
                onChange={(e) => setWinHistorySearch(e.target.value)}
                placeholder="Search by carrier or driver name..."
                className="w-full bg-transparent text-xs text-white placeholder-white/20 outline-none py-1"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }} />
            </div>
            <div className="divide-y divide-white/05 max-h-72 overflow-y-auto">
              {(() => {
                const q = winHistorySearch.trim().toLowerCase();
                const filtered = q ? winHistory.filter((h) =>
                  (h.carrier_name || "").toLowerCase().includes(q) ||
                  (h.driver_name || "").toLowerCase().includes(q) ||
                  (h.carrier_email || "").toLowerCase().includes(q)
                ) : winHistory;
                if (filtered.length === 0) return (
                  <p className="px-4 py-6 text-xs text-white/25 text-center">
                    {winHistory.length === 0 ? "No win emails sent yet." : "No results match your search."}
                  </p>
                );
                return filtered.map((h) => (
                  <div key={h.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-head text-sm font-bold text-white truncate">{h.carrier_name}</p>
                        <p className="text-[10px] text-white/40 mt-0.5">Driver: {h.driver_name} · Fee: <span style={{ color: GOLD }}>${(h.placement_fee || 0).toLocaleString()}</span></p>
                        <p className="text-[10px] text-white/25 mt-0.5">{h.carrier_email}</p>
                      </div>
                      <span className="text-[9px] text-white/25 shrink-0 mt-0.5">{timeAgo(h.sent_at)}</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* ── Manual Transfer Modal ── */}
    {showManualTransfer && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)" }}
        onClick={(e) => { if (e.target === e.currentTarget) setShowManualTransfer(false); }}>
        <div className="w-full max-w-md p-6 space-y-5" style={{ background: "#18181b", border: "1px solid rgba(34,197,94,0.25)" }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.3em] text-white/30 mb-0.5">Admin Tool</p>
              <h3 className="font-head text-lg font-black uppercase text-white">Manual Transfer</h3>
              <p className="text-[11px] text-white/40 mt-1">Transfer a driver to a carrier without a bid — use only to fix sync glitches.</p>
            </div>
            <button onClick={() => setShowManualTransfer(false)} className="text-white/30 hover:text-white p-1"><X className="w-4 h-4" /></button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Driver *</label>
              <select data-testid="mt-driver-select" value={mtDriverId}
                onChange={(e) => setMtDriverId(e.target.value)}
                className="w-full px-3 py-2 text-sm text-white font-head"
                style={{ background: "#111", border: "1px solid rgba(255,255,255,0.12)" }}>
                <option value="">— Select driver —</option>
                {(drivers || []).filter((d) => d.market_status !== "transferred").map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name} · {d.cdl_class || "CDL-A"} · {d.state || ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Carrier Company Name *</label>
              <input data-testid="mt-carrier-input" type="text" value={mtCarrier}
                onChange={(e) => setMtCarrier(e.target.value)}
                placeholder="e.g. Milstar Team Freight"
                className="w-full px-3 py-2 text-sm text-white font-head"
                style={{ background: "#111", border: "1px solid rgba(255,255,255,0.12)" }} />
            </div>

            <div>
              <label className="block font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Placement Fee Paid ($)</label>
              <input data-testid="mt-fee-input" type="number" min="0" value={mtFee}
                onChange={(e) => setMtFee(e.target.value)}
                placeholder="e.g. 1500"
                className="w-full px-3 py-2 text-sm text-white font-head"
                style={{ background: "#111", border: "1px solid rgba(255,255,255,0.12)" }} />
              <p className="text-white/25 text-[10px] mt-1">Leave blank or 0 if not yet paid</p>
            </div>

            <div>
              <label className="block font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Notes (optional)</label>
              <input data-testid="mt-notes-input" type="text" value={mtNotes}
                onChange={(e) => setMtNotes(e.target.value)}
                placeholder="e.g. Carrier paid via WhatsApp"
                className="w-full px-3 py-2 text-sm text-white font-head"
                style={{ background: "#111", border: "1px solid rgba(255,255,255,0.12)" }} />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={() => setShowManualTransfer(false)}
              className="flex-1 py-2.5 font-head text-[10px] uppercase tracking-[0.15em] border border-white/10 text-white/40 hover:border-white/20 transition-colors">
              Cancel
            </button>
            <button data-testid="mt-confirm-btn" onClick={handleManualTransfer} disabled={mtLoading || !mtDriverId || !mtCarrier.trim()}
              className="flex-1 py-2.5 font-head text-[10px] uppercase tracking-[0.15em] font-black flex items-center justify-center gap-2 disabled:opacity-40 hover:brightness-110 transition-all"
              style={{ background: "#22C55E18", color: "#22C55E", border: "1px solid #22C55E40" }}>
              {mtLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              Confirm Transfer
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
